import { getUploadImageKey } from '@onefootprint/core';
import { IcoEyeCrossed16 } from '@onefootprint/icons';
import type { DocumentFaceComparison } from '@onefootprint/request-types/dashboard';
import { LinkButton, LoadingSpinner, Tooltip, useObjectUrl } from '@onefootprint/ui';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import useCurrentEntity from '@/entities/components/details/hooks/use-current-entity';
import type useEntityVault from '@/entities/hooks/use-entity-vault';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import UploadImage from '../upload-image-item/components/upload-image';

type ReferenceImageProps = {
  comparison: DocumentFaceComparison;
  vault: ReturnType<typeof useEntityVault>;
};

const ReferenceImage = forwardRef<HTMLDivElement, ReferenceImageProps>(({ comparison, vault }, ref) => {
  const { t } = useTranslation('entity-details');
  const { data: entity } = useCurrentEntity();
  const decryptControls = useDecryptControls();
  const field = getUploadImageKey({ image: comparison.reference });
  const { objectUrl } = useObjectUrl((vault.data?.vault[field] as string | undefined) ?? null);

  const handleDecrypt = async () => {
    if (!entity) return;
    const reason = t($ => $.decrypt.documentDetailsReason);
    const response = await decryptControls.decrypt({ entity, fields: [field], reason });
    if (response) {
      vault.update({ vault: response, transforms: {}, dataKinds: {} });
    }
  };

  return (
    <div className="flex flex-col items-center w-full gap-2 scroll-mt-2" ref={ref}>
      <div className="flex items-center justify-between w-full gap-2 px-3 py-2 border border-solid rounded border-tertiary bg-primary">
        <span className="text-label-2 text-primary">{t($ => $.fieldset.documents.details.referenceImage.title)}</span>
        <Tooltip content={t($ => $.fieldset.documents.details.faceChecks.employeeOnly)} side="left">
          <div className="flex-shrink-0 text-tertiary">
            <IcoEyeCrossed16 />
          </div>
        </Tooltip>
      </div>
      {objectUrl ? (
        <UploadImage objectUrl={objectUrl} alt={field} failureReasons={[]} />
      ) : (
        <div className="flex items-center justify-center w-full h-40 border border-dashed rounded border-tertiary">
          {decryptControls.isDecrypting([field]) ? (
            <LoadingSpinner />
          ) : (
            <Tooltip
              disabled={comparison.canDecryptReference}
              content={t($ => $.fieldset.documents.details.decrypt.notAllowed)}
            >
              <LinkButton disabled={!comparison.canDecryptReference} onClick={handleDecrypt}>
                {t($ => $.fieldset.documents.details.decrypt.decryptCta)}
              </LinkButton>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
});

export default ReferenceImage;
